import { useState, useEffect, useMemo } from 'react'
import { PageHeader, StatusBadge, EmptyState, Modal } from '../components/UI'
import { get, post } from '../utils/api'
import { formatCurrency, STAGES } from '../utils/helpers'
import { IconBriefcase, IconPlus, IconTrendingUp, IconCurrencyDollar, IconTarget, IconX } from '@tabler/icons-react'

export default function DealsPage() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)

  const load = async () => { try { setDeals(await get('/deals')) } catch {} finally { setLoading(false) } }
  useEffect(() => { load() }, [])

  const stats = useMemo(() => ({
    total: deals.length,
    open: deals.filter(d => !['closed_won','closed_lost'].includes(d.stage)).length,
    pipeline: deals.reduce((s,d) => s + parseFloat(d.deal_value||0), 0),
    wonMtd: deals.filter(d => d.stage === 'closed_won').reduce((s,d) => s + parseFloat(d.deal_value||0), 0),
  }), [deals])

  const handleCreate = async () => {
    setSaving(true)
    try { await post('/deals', formData); setShowAdd(false); setFormData({}); load() }
    catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Deals" subtitle={`${deals.length} deals — ${formatCurrency(stats.pipeline)} total pipeline`}
        actions={<button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><IconPlus size={16} className="me-1" />New Deal</button>} />
      <div className="row row-cards mb-3">
        {[{l:'Total',v:stats.total,i:IconBriefcase,c:'primary'},{l:'Open',v:stats.open,i:IconTrendingUp,c:'blue'},
          {l:'Pipeline',v:formatCurrency(stats.pipeline),i:IconCurrencyDollar,c:'green'},{l:'Won MTD',v:formatCurrency(stats.wonMtd),i:IconTarget,c:'green'}
        ].map(s => (
          <div key={s.l} className="col-6 col-lg-3"><div className="card stat-mini"><div className="card-body">
            <div className="subheader">{s.l}</div><div className="h3 mb-0">{s.v}</div>
          </div></div></div>
        ))}
      </div>
      {deals.length === 0 ? <EmptyState icon={IconBriefcase} title="No deals found" message="Create a deal to start tracking your pipeline." /> : (
        <div className="row row-cards">
          {STAGES.map(stage => {
            const stageDeals = deals.filter(d => d.stage === stage.key)
            if (!stageDeals.length) return null
            return (
              <div key={stage.key} className="col-xl-4 col-lg-6">
                <div className="card">
                  <div className="card-header"><h3 className="card-title">{stage.label} <span className="badge bg-secondary-lt ms-2">{stageDeals.length}</span></h3></div>
                  <div className="list-group list-group-flush">
                    {stageDeals.map(d => (
                      <div key={d.id} className="list-group-item">
                        <div className="fw-medium">{d.deal_name}</div>
                        <div className="text-secondary" style={{fontSize:'0.8rem'}}>{d.company} — {d.contact_name}</div>
                        <div className="d-flex justify-content-between mt-1">
                          <span className="fw-bold">{formatCurrency(d.deal_value)}</span>
                          <span className="text-secondary">{d.probability}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="New Deal" wide>
        <div className="row g-3">
          {[{label:'Deal Name',key:'deal_name'},{label:'Company',key:'company'},{label:'Contact',key:'contact_name'},
            {label:'Value',key:'deal_value',type:'number'},{label:'Probability %',key:'probability',type:'number'},
            {label:'Expected Close',key:'expected_close_date',type:'date'}].map(f => (
            <div key={f.key} className="col-md-6"><label className="form-label">{f.label}</label>
              <input type={f.type||'text'} className="form-control" value={formData[f.key]||''} onChange={e => setFormData({...formData,[f.key]:e.target.value})} /></div>))}
          <div className="col-md-6"><label className="form-label">Stage</label>
            <select className="form-select" value={formData.stage||''} onChange={e => setFormData({...formData,stage:e.target.value})}>
              <option value="">Select...</option>{STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}</select></div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleCreate}>{saving?'Creating...':'Create'}</button>
        </div>
      </Modal>
    </>
  )
}
